// import apiService from './apiService';
// import imageService from './imageService';
import store from '@/store';
import axios from 'axios';

// 작업별 서비스 임포트
import mainImageService from './works/mainImageService';
import additionalImageService from './works/additionalImageService';
import detailImageService from './works/detailImageService';
import seoService from './works/seoService';
import { seoErrorFields, koreanLabelErrorFields } from './works/seoErrorKeep';
import sizeChartService from './works/sizeChartService';
import koreanLabelService from './works/koreanLabelService';
import pricingService from './works/pricingService';
import quotationService from './works/quotationService';
import { makerFieldsMissingError } from './works/quotationManufacturer';
import PracticalSchemaValidator from './validation/schemaValidator';
import { markStepStart, markStepEnd } from './works/workProgress';
import { isLightDoc, LIGHT_SAVE_ERROR } from '../utils/rocketRegLight';
import { withTimeout, TASK_TIMEOUT } from '../utils/withTimeout';
import { logServiceError, ERROR_TYPES } from './works/errorLoggingService';

// 확장 프로그램 요청 관리를 위한 맵
const extensionRequests = new Map();

// 안전망 — 받은 상품이나 store 의 같은 상품이 목록용(_light)이면 완전 문서로 받아 store 것을 돌려준다(실패하면 throw).
// 화면이 미리 받아 오지만, 그 사이 목록을 새로 읽으면 store 가 다시 목록용이 된다.
async function ensureFullProduct(product) {
    if (!product) return product;
    const same = () => store.state.rocketProducts.find(p => p.registrationId === product.registrationId);
    const inStore = same();
    if (!isLightDoc(product) && !isLightDoc(inStore)) return product;
    const [full] = await store.dispatch('ensureFullRocketProducts', [(inStore || product)._id]);
    // 반쪽 문서로 조용히 계속하지 않는다
    if (!full || isLightDoc(full)) throw new Error(LIGHT_SAVE_ERROR);
    return full;
}

// 견적서 앞단 점검(precheckBeforeQuotation) 켜기 — false 면 견적서 뒤 재시도(imageTasksForValidationErrors)만 남는다
const QUOTATION_PRECHECK = true;

// executeSingleTask 가 화면 목록(store)에서 상품을 못 찾았을 때 던지는 오류 표식
const PRODUCT_NOT_IN_LIST = 'PRODUCT_NOT_IN_LIST';

// 이 화면에서 지금 돌고 있는 작업 — `${registrationId}:${task}`. 견적서는 같은 상품의 다른 작업이 끝난 뒤에 돈다(waitForRunningTasks).
const runningTasks = new Set();

// Rate Limiting 관련 상수 및 상태 변수
const RATE_LIMIT_PER_MINUTE = 12;  // 무료 등급 RPM 제한 (Gemini-2.0-flash) - 안전 여유분 고려
const MINUTE_IN_MS = 60 * 1000;    // 1분을 밀리초로 표현

// 요청 큐 및 상태 관리
const requestQueue = [];           // 대기 중인 요청 큐
let processingRequest = false;     // 현재 요청 처리 중 여부
let requestTimestamps = [];        // 요청 타임스탬프 배열 (롤링 윈도우 방식)

/**
 * 작업 흐름 제어 서비스
 * 로켓배송 상품 등록 작업의 흐름을 관리하고 제어합니다.
 */
const workflowService = {
    /**
     * 선택된 상품들에 대한 작업 처리를 시작합니다.
     * @param {Array} selectedProducts - 처리할 선택된 상품 배열
     * @param {Object} options - 작업 옵션 (선택적)
     * @returns {Promise<Object>} 작업 결과
     */
    async startProcessing(selectedProducts, options = {}) {
        if (!selectedProducts || selectedProducts.length === 0) {
            throw new Error('처리할 상품이 선택되지 않았습니다.');
        }

        // Gemini API 키 확인
        if (!store.state.settings[0]?.hasGeminiKey) {
            throw new Error('Gemini API Key가 설정되지 않았습니다. 설정에서 API Key를 먼저 설정해주세요.');
        }

        // 작업 상태 업데이트
        store.dispatch('setProcessing', { isProcessing: true, message: '작업 초기화 중...' });

        try {
            // 상품별 순차 처리
            for (let i = 0; i < selectedProducts.length; i++) {
                const product = selectedProducts[i];

                store.dispatch('setProcessing', {
                    isProcessing: true,
                    message: `상품 ${i + 1}/${selectedProducts.length} 처리 중...`
                });

                await this.processProduct(product, options);
            }

            store.dispatch('setProcessing', { isProcessing: false });
            return { success: true, message: '선택한 상품의 작업이 완료되었습니다.' };
        } catch (error) {
            console.error('작업 처리 중 오류:', error);
            store.dispatch('setProcessing', { isProcessing: false });
            throw error;
        }
    },
    async processProduct(product, tasksToProcess = null, isRetry = false) {
        product = await ensureFullProduct(product);
        // 작업 유형과 상태 필드 간의 매핑
        const taskToStatusField = {
            'seo': 'seoStatus',
            'pricing': 'pricingStatus',
            'mainImage': 'mainImageStatus',
            'additionalImage': 'additionalImageStatus',
            'detailImage': 'detailImageStatus',
            'koreanLabel': 'koreanLabelStatus',
            'sizeChart': 'sizeChartStatus',
            'quotation': 'quotationStatus'
        };

        // 작업 완료 여부 확인 헬퍼 함수
        const isTaskCompleted = (task, prod) => this.isTaskCompleted(task, prod);

        // 작업 목록 결정
        let tasks = [];

        if (tasksToProcess === null) {
            // 기본: 완료되지 않은 작업만 실행 (기존 excludeCompleted 동작)
            const allTasks = [
                'seo', 'pricing', 'mainImage', 'additionalImage',
                'detailImage', 'koreanLabel', 'sizeChart', 'quotation'
            ];

            tasks = allTasks.filter(task => !isTaskCompleted(task, product));
            console.log(`🚀 전체 작업 모드: 완료 안된 ${tasks.length}개 작업 실행 예정 -`, tasks);
        } else if (Array.isArray(tasksToProcess)) {
            tasks = tasksToProcess;
        } else {
            tasks = [tasksToProcess];
        }

        // quotation만 따로 실행하는 경우를 구분
        const isQuotationOnly = tasks.length === 1 && tasks[0] === 'quotation';

        // quotation 분리 (마지막에 실행)
        const hasQuotation = tasks.includes('quotation');
        if (hasQuotation && !isQuotationOnly) {
            tasks = tasks.filter(t => t !== 'quotation');
        }

        try {
            if (isRetry) {
                console.log(`🔄 재시도 모드: ${tasks.length}개 작업 재처리 시작 (상품: ${product.registrationId})`);
            }

            // ========== SEO와 Pricing은 순차 실행 (선행 조건) ==========
            const seqTasks = ['seo', 'pricing'].filter(t => tasks.includes(t));
            for (const task of seqTasks) {
                await this.executeSingleTask(product, task, taskToStatusField, isRetry);
            }

            // ========== 이미지 관련 작업들 병렬 실행 ==========
            const parallelTasks = tasks.filter(t => !['seo', 'pricing', 'quotation'].includes(t));

            if (parallelTasks.length > 0) {
                console.log(`🔀 병렬 처리 시작: ${parallelTasks.length}개 작업 -`, parallelTasks);

                store.dispatch('setProcessing', {
                    isProcessing: true,
                    message: `이미지 작업 병렬 처리 중... (${parallelTasks.length}개)`
                });

                // MainImage → AdditionalImage 체인 (의존성)
                const mainImageChain = async () => {
                    if (parallelTasks.includes('mainImage')) {
                        await this.executeSingleTask(product, 'mainImage', taskToStatusField, isRetry);
                    }
                    if (parallelTasks.includes('additionalImage')) {
                        await this.executeSingleTask(product, 'additionalImage', taskToStatusField, isRetry);
                    }
                };

                // DetailImage 독립 실행
                const detailImageTask = async () => {
                    if (parallelTasks.includes('detailImage')) {
                        await this.executeSingleTask(product, 'detailImage', taskToStatusField, isRetry);
                    }
                };

                // SizeChart 독립 실행
                const sizeChartTask = async () => {
                    if (parallelTasks.includes('sizeChart')) {
                        await this.executeSingleTask(product, 'sizeChart', taskToStatusField, isRetry);
                    }
                };

                // KoreanLabel 독립 실행
                const koreanLabelTask = async () => {
                    if (parallelTasks.includes('koreanLabel')) {
                        await this.executeSingleTask(product, 'koreanLabel', taskToStatusField, isRetry);
                    }
                };

                // 병렬 실행
                const parallelPromises = [
                    mainImageChain(),
                    detailImageTask(),
                    sizeChartTask(),
                    koreanLabelTask()
                ];

                const results = await Promise.allSettled(parallelPromises);

                // 실패한 작업 로깅
                results.forEach((result, index) => {
                    if (result.status === 'rejected') {
                        console.error(`병렬 작업 ${index} 실패:`, result.reason);
                    }
                });
                // 상품이 목록에서 빠져 못 돈 작업은 삼키지 않는다 — 견적서까지 가지 않고 실패로 올린다
                const missing = results.find(r => r.status === 'rejected' && r.reason && r.reason.code === PRODUCT_NOT_IN_LIST);
                if (missing) throw missing.reason;
            }

            // ========== Quotation은 모든 작업 완료 후 실행 ==========
            if (hasQuotation && !isQuotationOnly) {
                console.log('🔍 quotation 실행 전 모든 작업 상태 확인');

                // 최신 상품 정보 가져오기
                let currentProduct = store.state.rocketProducts.find(p =>
                    p.registrationId === product.registrationId
                ) || product;

                // quotation을 제외한 모든 작업들의 완료 상태 확인
                const allOtherTasks = ['seo', 'pricing', 'mainImage', 'additionalImage', 'detailImage', 'koreanLabel', 'sizeChart'];
                const failedTasks = [];

                for (const reqTask of allOtherTasks) {
                    if (reqTask === 'sizeChart' && !this.isSizeChartRequired(currentProduct)) {
                        continue;
                    }
                    if (!isTaskCompleted(reqTask, currentProduct)) {
                        failedTasks.push(reqTask);
                    }
                }

                if (failedTasks.length > 0 && !isRetry) {
                    console.log(`🔄 quotation 실행 전 미완료 작업 ${failedTasks.length}개 재시도:`, failedTasks);
                    await this.processProduct(currentProduct, failedTasks, true);

                    // 재시도 후 최신 상품 정보 다시 가져오기
                    currentProduct = store.state.rocketProducts.find(p =>
                        p.registrationId === product.registrationId
                    ) || currentProduct;
                }

                // Quotation 실행
                await this.executeSingleTask(currentProduct, 'quotation', taskToStatusField, isRetry);

                // 견적서 최종검증이 이미지 칸 누락으로 실패하면 그 칸을 만드는 작업을 한 번 다시 돌리고 견적서를 한 번 더.
                // 견적서 자체 재시도(quotationService analyzeValidationErrors)는 「…가 필수입니다」를 재시도 대상으로 안 치고,
                // 쳐도 견적서만 다시 돌려 같은 칸이 또 빈다(2026-10-08 DITCO 261008001001·002001 — 2회 모두 같은 누락으로 실패).
                if (!isRetry) {
                    const afterQuotation = store.state.rocketProducts.find(p => p.registrationId === product.registrationId) || currentProduct;
                    const redo = this.imageTasksForValidationErrors(afterQuotation);
                    if (redo.length) {
                        console.error(`[workflow] 견적서 최종검증 이미지 누락 — ${redo.join(', ')} 다시 돌린 뒤 견적서 재실행 (상품: ${product.registrationId})`);
                        await this.processProduct(afterQuotation, [...redo, 'quotation'], true);
                    }
                }
            } else if (isQuotationOnly) {
                await this.executeSingleTask(product, 'quotation', taskToStatusField, isRetry);
            }

            // 모든 작업 완료
            console.log(`✅ 모든 작업 완료 (상품: ${product.registrationId})`);
            store.dispatch('setProcessing', { isProcessing: false });

            return true;
        } catch (error) {
            console.error('작업 처리 중 오류 발생:', error);
            store.dispatch('setProcessing', { isProcessing: false });
            throw error;
        }
    },

    /**
     * 같은 상품의 다른 작업(다른 작업개시 흐름이 돌린 것 포함)이 끝날 때까지 기다린다 — 견적서가 아직 안 만들어진 이미지 칸을 읽지 않게.
     * 2026-10-08 DITCO 261008002001: 상세이미지 작업 중(번역 08:41:59~08:43:28, 업로드 08:43:55)에 견적서가 08:43:23 시작 → 08:43:29 검증 실패.
     */
    // 한도 뒤에도 남은 작업 목록을 돌려준다(빈 목록 = 다 끝남) — 남았는데 견적서를 돌리면 같은 상품을 두 번 나눈다(2B6AC5)
    async waitForRunningTasks(registrationId, maxMs = this.runningWaitMs()) {
        const busy = () => [...runningTasks].filter(k => k.startsWith(`${registrationId}:`));
        const start = Date.now();
        if (busy().length) console.error(`[workflow] 견적서 대기 — 같은 상품 작업이 아직 도는 중: ${busy().join(', ')}`);
        while (busy().length && Date.now() - start < maxMs) {
            await new Promise(resolve => setTimeout(resolve, Math.min(1000, maxMs)));
        }
        return busy();
    },

    // 견적서가 같은 상품 작업을 기다리는 한도
    runningWaitMs() {
        return 15 * 60 * 1000;
    },

    // 한도를 넘긴 작업의 잠금을 속 작업이 끝날 때까지 쥐는 상한 — 끝내 안 끝나면(탭 뒤 html2canvas) 이 뒤 강제로 푼다
    heldLockMaxMs() {
        return 30 * 60 * 1000;
    },

    // 한도를 넘긴 작업(withTimeout 은 속 작업을 못 멈춤)의 잠금은 속 작업이 정말 끝날 때 푼다.
    // 먼저 풀면 다시 부른 runWork 가 같은 견적서를 또 돌려 분리 조각이 겹친다(moyacompany PR261021-2B6AC5 · C9F613).
    releaseWhenSettled(runKey, work) {
        const since = Date.now();
        let cap = null;
        let freed = false;
        const free = (why) => {
            if (freed) return;
            freed = true;
            clearTimeout(cap);
            runningTasks.delete(runKey);
            const afterSec = Math.round((Date.now() - since) / 1000);
            const line = `${runKey} 잠금 풂 ${new Date().toISOString()} — ${why}(한도 넘긴 뒤 ${afterSec}초)`;
            console.error(`[workflow] ${line}`);
            // 셀러 콘솔만이면 셀 수 없다 — 배포 뒤 «강제로 풂» 이 몇 번 나는지 서버에서 센다(splitreason-1)
            logServiceError('workflowService', 'releaseWhenSettled', line, {
                errorType: ERROR_TYPES.PROCESSING_ERROR,
                productId: runKey.split(':')[0],
                additional: { runKey, why, afterSec }
            });
        };
        cap = setTimeout(() => free('속 작업이 끝내 안 끝나 강제로 풂'), this.heldLockMaxMs());
        work.then(() => free('한도 넘긴 속 작업이 끝남'), () => free('한도 넘긴 속 작업이 실패로 끝남'));
    },

    // 전송 검사(validateBasicRequiredFields)의 이미지 칸(메인·상세·대체텍스트) 오류 — 옵션 문서별
    imageFieldErrors(prod) {
        const validator = new PracticalSchemaValidator('null');
        const docs = (prod && prod.coupangDoc && prod.coupangDoc.jsonDocument) || [];
        return docs.map((doc, i) => {
            const errs = validator.validateBasicRequiredFields(doc || {}).errors.filter(err => err.includes('이미지'));
            return errs.length ? `옵션 ${i + 1}: ${errs.join(', ')}` : null;
        }).filter(Boolean);
    },

    imageTasksForErrors(errorList) {
        // 대체텍스트는 상세이미지 작업이 쓴다
        const errors = errorList.join(' ');
        const tasks = [];
        if (errors.includes('메인 이미지가 필수')) tasks.push('mainImage');
        if (errors.includes('상세 이미지가 필수') || errors.includes('이미지 대체 텍스트가 필수')) tasks.push('detailImage');
        return tasks;
    },

    /**
     * 견적서 앞단 점검(방어) — 이미지 칸이 비었으면 그 작업을 한 번 돌리고, 그래도 비면 사유 문자열(견적서 만들지 않음). 다 차면 null.
     * 브랜드·제조사가 비게 되면(설정 제조사·브랜드 빈 계정) 견적서를 만들지 않고 채울 곳을 말한다 — 쿠팡 «제조사 필수»·«브랜드 필수» 거절 전에(804E1C·889CD0).
     */
    async precheckBeforeQuotation(prod, taskToStatusField) {
        const noMaker = makerFieldsMissingError(prod, (store.state.settings[0] || {}).defaultSettings);
        if (noMaker) return this.savePrecheckError(prod, [noMaker]);
        const tasks = this.imageTasksForErrors(this.imageFieldErrors(prod));
        if (!tasks.length) return null;
        console.error(`[workflow] 견적서 앞단 점검 — 빈 이미지 칸 → ${tasks.join(', ')} 먼저 실행 (상품: ${prod.registrationId})`);
        for (const task of tasks) {
            await this.executeSingleTask(prod, task, taskToStatusField, true);
        }
        const latest = store.state.rocketProducts.find(p => p.registrationId === prod.registrationId) || prod;
        const left = this.imageFieldErrors(latest);
        if (!left.length) return null;
        return this.savePrecheckError(latest, left);
    },

    /** SEO 실패 사유를 상품 seoError 에 남긴다(작업개시 결과 reason 이 읽는다 · processError 는 견적서 몫이라 안 씀) — 저장 실패는 로그만 남기고 작업 흐름은 그대로 */
    async saveSeoError(prod, message) {
        try {
            await store.dispatch('updateProductPartial', { productId: prod._id, updateFields: seoErrorFields(message, Date.now()) });
        } catch (err) {
            console.error('[workflow] SEO 실패 사유 저장 실패:', err);
        }
    },

    /** 한글표시 실패 사유를 상품 koreanLabelError 에 남긴다(작업개시 결과 reason 이 읽는다 · 윈 3469 «그리기 3분» 사유가 결과에 안 갔다) — 저장 실패는 로그만 */
    async saveKoreanLabelError(prod, message) {
        try {
            await store.dispatch('updateProductPartial', { productId: prod._id, updateFields: koreanLabelErrorFields(message, Date.now()) });
        } catch (err) {
            console.error('[workflow] 한글표시 실패 사유 저장 실패:', err);
        }
    },

    /** 같은 상품 작업이 아직 돌아 견적서를 시작하지 않은 사유를 상품에 남긴다(도구 결과 quotationError 가 processError 를 읽는다) */
    async saveRunningError(prod, running) {
        try {
            await store.dispatch('updateProductPartial', {
                productId: prod._id,
                updateFields: { processError: { message: '같은 상품 작업이 아직 도는 중이라 견적서를 시작하지 않았습니다 — 끝난 뒤 다시 돌려주세요', errors: running, running: true, timestamp: Date.now() } }
            });
        } catch (err) {
            console.error('[workflow] 견적서 안 시작 사유 저장 실패:', err);
        }
    },

    /** 앞단 점검 멈춤 사유를 상품에 남기고(도구 응답 quotationError·전송 사유가 읽는다) 사유 문자열을 돌려준다 */
    async savePrecheckError(prod, errors) {
        try {
            await store.dispatch('updateProductPartial', {
                productId: prod._id,
                updateFields: { processError: { message: '견적서 앞단 점검 실패', errors, precheck: true, timestamp: Date.now() } }
            });
        } catch (err) {
            console.error('[workflow] 앞단 점검 사유 저장 실패:', err);
        }
        return `견적서 앞단 점검: 채우지 못한 칸 — ${errors.join(' / ')}`;
    },

    imageTasksForValidationErrors(prod) {
        // 견적서가 error 이고 최종검증 오류가 이미지 칸 누락이면 그 칸을 만드는 작업
        // 앞단 점검이 이미 한 번 돌리고도 못 채워 멈춘 경우는 다시 돌리지 않는다
        if (!prod || prod.quotationStatus !== 'error') return [];
        if (prod.processError && prod.processError.precheck) return [];
        return this.imageTasksForErrors((prod.processError && Array.isArray(prod.processError.errors)) ? prod.processError.errors : []);
    },

    /**
     * 작업 완료 여부 — processProduct 가 "완료 안 된 작업만" 고를 때 쓰는 기준.
     * CLI(rocket.sourcing.runWork)도 같은 기준으로 건너뛸 작업을 고르려고 밖으로 뺐다.
     */
    isTaskCompleted(task, prod) {
        const status = prod[`${task}Status`];
        if (typeof status === 'object') {
            return status.status === 'completed';
        }
        return status === 'completed' || status === 'disabled';
    },

    // 목록에 없는 상품의 작업 상태를 서버에 'error' 로 남긴다(목록에 없으니 updateFeatureStatus 처럼 상품 통째로 덮지 않는다)
    async markTaskErrorOutsideList(product, statusField) {
        if (!product || !product._id) {
            console.error('[workflow] 목록에 없는 상품 — _id 가 없어 오류 상태를 남기지 못했습니다:', product && product.registrationId);
            return;
        }
        const field = product[statusField] && typeof product[statusField] === 'object' ? `${statusField}.status` : statusField;
        try {
            await store.dispatch('updateProductPartial', { productId: product._id, updateFields: { [field]: 'error', updatedAt: Date.now() } });
        } catch (err) {
            console.error('[workflow] 목록에 없는 상품 오류 상태 저장 실패:', product.registrationId, err);
        }
    },

    /**
     * 작업 하나의 기다림 한도(ms) — 넘으면 executeSingleTask 가 그 작업을 error 로 두고 다음으로 간다.
     * 대표이미지는 옵션마다 직렬로 돌아 옵션 수에 비례(10-19 A89E17 48옵션) · 견적서는 옵션별 직렬 폴백 몫으로 길게.
     */
    taskTimeoutMs(task, product) {
        const MIN = 60 * 1000;
        if (task === 'seo' || task === 'pricing') return 10 * MIN;
        if (task === 'quotation') return 30 * MIN;
        if (task === 'mainImage') return Math.max(15 * MIN, ((product && product.options && product.options.length) || 0) * 2 * MIN);
        return 15 * MIN;
    },

    /**
     * 상품 하나의 작업 한도 합(ms) — 화면 박동이 진척 없이 기다려 줄 길이(utils/cliHeartbeat.stallLimitMs)에 쓴다.
     * 병렬 이미지 단계까지 그냥 더해 넉넉히 잡는다(실제 한도보다 짧으면 큰 상품이 박동 멈춤으로 끊긴다).
     */
    productTimeoutMs(product, tasks) {
        return (tasks || []).reduce((sum, task) => sum + this.taskTimeoutMs(task, product), 0);
    },

    /**
     * 단일 작업 실행 헬퍼 메서드
     */
    async executeSingleTask(product, task, taskToStatusField, isRetry = false) {
        product = await ensureFullProduct(product);
        const statusField = taskToStatusField[task];
        if (!statusField) {
            console.warn(`알 수 없는 작업 유형: ${task}`);
            return;
        }

        // 최신 상품 정보 가져오기
        let currentProduct = store.state.rocketProducts.find(p =>
            p.registrationId === product.registrationId
        );

        if (!currentProduct) {
            // 화면 목록(store)에서 빠진 상품 — 조용히 return 하면 그 작업이 pending 인 채 작업개시가 «완료»로 끝났다
            // (PR261017-62FA8B: SEO 만 되고 가격·이미지·견적서 pending, 대기열 done). 상품에 error 를 남기고 던져서
            // 부르는 쪽(autoFullProcess·cliPageRunWork)이 실패로 센다.
            console.error('제품을 찾을 수 없습니다:', product.registrationId, task);
            await this.markTaskErrorOutsideList(product, statusField);
            const err = new Error(`화면 목록에서 상품을 찾지 못해 '${this.getWorkTypeName(statusField)}' 작업을 하지 못했습니다(등록ID ${product.registrationId}) — 화면을 새로고침한 뒤 다시 돌려주세요`);
            err.code = PRODUCT_NOT_IN_LIST;
            throw err;
        }

        if (isRetry) {
            console.log(`🔄 재시도 작업: ${task}`);
        }

        // 견적서는 같은 상품의 다른 작업(다른 작업개시 흐름 포함)이 끝난 뒤에 — 이미지 칸이 아직 안 찬 문서를 읽지 않게
        if (task === 'quotation') {
            const still = await this.waitForRunningTasks(product.registrationId);
            if (still.length) {
                console.error(`[workflow] ${product.registrationId} 같은 상품 작업(${still.join(', ')})이 아직 도는 중이라 견적서를 시작하지 않았습니다 — 끝난 뒤 다시 돌려주세요`);
                await this.saveRunningError(currentProduct, still);
                await store.dispatch('updateFeatureStatus', { product: currentProduct, feature: task, status: 'error' });
                return;
            }
            currentProduct = store.state.rocketProducts.find(p => p.registrationId === product.registrationId) || currentProduct;
        }
        const runKey = `${product.registrationId}:${task}`;
        runningTasks.add(runKey);
        markStepStart(product.registrationId, task);
        let work = null;
        let timedOut = false;

        try {
            // 'processing' 상태로 업데이트
            await store.dispatch('updateFeatureStatus', {
                product: currentProduct,
                feature: task,
                status: 'processing'
            });

            store.dispatch('setProcessing', {
                isProcessing: true,
                message: `${this.getWorkTypeName(task)} 처리 중...`
            });

            // 작업 처리 결과를 저장하여 상태 확인 — 작업별 한도를 넘으면 TASK_TIMEOUT 으로 던져 아래 catch 가 error 로 남긴다
            // (10-20 윈 outbox 3255: html2canvas 가 안 끝나 작업개시가 영원히 running)
            const result = await withTimeout(work = (async () => {
            let result = null;

            // 작업 처리
            switch (task) {
                case 'seo':
                    result = await seoService.process(currentProduct);
                    break;
                case 'pricing':
                    result = await pricingService.process(currentProduct);
                    break;
                case 'mainImage':
                    result = await mainImageService.processAllOptions(currentProduct);
                    break;
                case 'additionalImage':
                    result = await additionalImageService.process(currentProduct);
                    break;
                case 'detailImage':
                    result = await detailImageService.process(currentProduct);
                    break;
                case 'sizeChart':
                    if (this.isSizeChartRequired(currentProduct)) {
                        result = await sizeChartService.process(currentProduct);
                    } else {
                        console.log('사이즈표가 필요하지 않은 카테고리입니다. 건너뜁니다.');
                        await store.dispatch('updateFeatureStatus', {
                            product: currentProduct,
                            feature: task,
                            status: 'disabled'
                        });
                        result = { status: 'disabled' };
                    }
                    break;
                case 'koreanLabel':
                    result = await koreanLabelService.processKoreanLabels(currentProduct);
                    break;
                case 'quotation':
                    var latestProduct = store.state.rocketProducts.find(p =>
                        p.registrationId === product.registrationId
                    );
                    // 견적서 앞단 점검 — 전송 검사가 보는 칸이 비었으면 그 칸 작업을 먼저 돌리고, 그래도 비면 견적서를 만들지 않는다
                    if (QUOTATION_PRECHECK) {
                        const precheckError = await this.precheckBeforeQuotation(latestProduct || currentProduct, taskToStatusField);
                        if (precheckError) {
                            result = { status: 'error', error: precheckError };
                            break;
                        }
                        latestProduct = store.state.rocketProducts.find(p => p.registrationId === product.registrationId);
                    }
                    result = await this.executeQuotationWithRetry(latestProduct || currentProduct);
                    break;
                default:
                    throw new Error(`알 수 없는 작업 유형: ${task}`);
            }
            return result;
            })(), this.taskTimeoutMs(task, currentProduct), `${this.getWorkTypeName(statusField)} 작업`);

            // ✅ 서비스 반환값의 status 확인 - error인 경우 상태 업데이트
            if (result && result.status === 'error') {
                console.error(`❌ 작업 '${task}' 에러 상태 반환:`, result.error || '알 수 없는 오류');
                await store.dispatch('updateFeatureStatus', {
                    product: currentProduct,
                    feature: task,
                    status: 'error'
                });
                if (task === 'seo') await this.saveSeoError(currentProduct, result.error);
                if (task === 'koreanLabel') await this.saveKoreanLabelError(currentProduct, result.error);
                // 에러 상태지만 throw하지 않고 다음 작업 진행 (다른 작업에 영향 최소화)
                console.log(`⚠️ 작업 '${task}' 에러 상태로 완료, 다음 작업 진행`);
            } else {
                console.log(`✅ 작업 ${task} 처리 완료`);
            }
        } catch (error) {
            console.error(`작업 '${task}' 처리 중 오류 발생:`, error);
            if (error && error.code === TASK_TIMEOUT) timedOut = true;

            // 에러 발생 시 상태를 'error'로 업데이트
            await store.dispatch('updateFeatureStatus', {
                product: currentProduct,
                feature: task,
                status: 'error'
            });
            if (task === 'seo') await this.saveSeoError(currentProduct, error && error.message);
            if (task === 'koreanLabel') await this.saveKoreanLabelError(currentProduct, error && error.message);

            if (task === 'seo' && error.message && error.message.startsWith('GEMINI_API_ERROR:')) {
                throw error;
            }
        } finally {
            if (timedOut && work) this.releaseWhenSettled(runKey, work);
            else runningTasks.delete(runKey);
            markStepEnd(product.registrationId, task);
        }
    },


    /**
     * quotation 작업을 재시도 로직과 함께 실행합니다.
     * @param {Object} product - 상품 객체
     * @returns {Promise<void>}
     */
    async executeQuotationWithRetry(product) {
        const maxRetries = 1; // 최대 1번 재시도 (총 2번 시도: 초기 + 재시도 1회)
        let attempt = 0;
        let lastError = null;

        while (attempt <= maxRetries) {
            try {
                console.log(`🔄 quotation 작업 시도 ${attempt + 1}/${maxRetries + 1} (상품: ${product.registrationId})`);

                if (attempt > 0) {
                    // 재시도인 경우 잠시 대기
                    const delay = 3000; // 3초 고정 대기
                    console.log(`⏳ quotation 재시도 전 ${delay}ms 대기...`);
                    await new Promise(resolve => setTimeout(resolve, delay));

                    // 최신 상품 정보 다시 가져오기
                    const latestProduct = store.state.rocketProducts.find(p =>
                        p.registrationId === product.registrationId
                    );
                    if (latestProduct) {
                        product = latestProduct;
                    }
                }

                // quotationService.process 실행
                const result = await quotationService.process(product);

                // 성공 시 결과 확인
                if (result && (result.status === 'completed' || result.status === 'partial' || result.status === 'split_completed')) {
                    console.log(`✅ quotation 작업 성공 (시도 ${attempt + 1}/${maxRetries + 1}):`, result.status);
                    return result;
                } else {
                    throw new Error(`quotation 작업 결과가 예상과 다름: ${result?.status || 'unknown'}`);
                }

            } catch (error) {
                lastError = error;
                attempt++;

                console.error(`❌ quotation 작업 실패 (시도 ${attempt}/${maxRetries + 1}):`, error.message);

                if (attempt <= maxRetries) {
                    console.log(`🔄 quotation 작업 재시도 예정 (${maxRetries + 1 - attempt}번 남음)`);
                } else {
                    console.error(`💥 quotation 작업 최종 실패 (${maxRetries + 1}번 모두 실패)`);
                    throw lastError;
                }
            }
        }
    },



    isSizeChartRequired(product) {
        try {
            if (product.rocketRegInfo && product.rocketRegInfo.schemaString) {
                const schema = JSON.parse(product.rocketRegInfo.schemaString);

                if (schema.properties &&
                    schema.properties.imagePage &&
                    schema.properties.imagePage.properties &&
                    schema.properties.imagePage.properties.images &&
                    schema.properties.imagePage.properties.images.properties &&
                    schema.properties.imagePage.properties.images.required) {

                    return schema.properties.imagePage.properties.images.required.includes('sizeChartImage');
                }
            }
        } catch (error) {
            console.error('스키마 파싱 오류:', error);
        }
        return false;
    },
    // workflowService.js - processWorkType 메서드 수정
    async processWorkType(product, type) {
        // 작업 시작 전 상태를 'processing'으로 업데이트
        await this.updateWorkStatus(product, type, 'processing');

        store.dispatch('setProcessing', {
            isProcessing: true,
            message: `${this.getWorkTypeName(type)} 처리 중...`
        });

        try {
            let result = null;

            // 각 작업 타입에 맞는 서비스 호출
            switch (type) {
                case 'seoStatus':
                    result = await seoService.process(product);
                    break;
                case 'pricingStatus':
                    result = await pricingService.process(product);
                    break;
                case 'mainImageStatus':
                    result = await mainImageService.processAllOptions(product);
                    break;
                case 'additionalImageStatus':
                    result = await additionalImageService.process(product);
                    break;
                case 'detailImageStatus':
                    result = await detailImageService.process(product);
                    break;
                case 'sizeChartStatus':
                    if (this.isSizeChartRequired(product)) {
                        result = await sizeChartService.process(product);
                    } else {
                        // 필요 없음으로 처리
                        await this.updateWorkStatus(product, type, 'disabled');
                    }
                    break;
                case 'koreanLabel':
                    // koreanLabelService.processKoreanLabels(updatedProduct) 대신
                    result = await store.dispatch('requestKoreanLabelData', {
                        product: updatedProduct
                    });
                    break;
                case 'quotationStatus':
                    result = await quotationService.process(product);
                    break;
                default:
                    console.error(`알 수 없는 작업 유형: ${type}`);
                    throw new Error(`알 수 없는 작업 유형: ${type}`);
            }

            // 여기가 수정된 부분: 작업 완료 후 최신 제품 정보를 가져옴
            const updatedProduct = store.state.rocketProducts.find(p => p.registrationId === product.registrationId);

            // 작업 완료 후 상태를 결과에 따라 업데이트 (최신 제품 정보 사용)
            const status = result && result.status ? result.status : 'completed';
            await this.updateWorkStatus(updatedProduct || product, type, status);

            // 작업 완료 상태로 업데이트
            store.dispatch('setProcessing', { isProcessing: false });

            console.log(`작업 ${type} 최종 결과:`, result);
            console.log('업데이트된 제품 상태:', (updatedProduct || product)[type]);

            return result;
        } catch (error) {
            console.error(`${type} 처리 중 오류 발생:`, error);
            // 오류 발생 시 상태를 'error'로 업데이트
            await this.updateWorkStatus(product, type, 'error');
            store.dispatch('setProcessing', { isProcessing: false });
            throw error;
        }
    },
    // workflowService.js - updateWorkStatus 메서드 수정
    async updateWorkStatus(product, type, status) {
        console.log('product update:', product);
        if (!product || !product.registrationId) return;

        console.log(`상품 ${product.registrationId} 상태 업데이트 시작: ${type} => ${status}`);

        // 상품 깊은 복사 (JSON.stringify와 parse 사용)
        const updatedProduct = JSON.parse(JSON.stringify(product));
        // 이전 상태 기록
        const previousStatus = updatedProduct[type];

        // 상태 필드 직접 업데이트
        updatedProduct[type] = status;

        console.log(`상품 상태 변경: ${previousStatus} -> ${status}`);

        // 이 작업의 상태 칸만 부분 저장한다 — 상품 통째(saveItems)로 보내면 함께 돌던 다른 작업이
        // 그 사이 저장한 칸(상세이미지 등)을 이 시점 사본으로 덮어쓴다
        const updateFields = { [type]: status };

        // 각 옵션의 해당 상태도 업데이트
        if (updatedProduct.options && updatedProduct.options.length > 0) {
            console.log(`${updatedProduct.options.length}개 옵션 상태 업데이트 시작`);

            updatedProduct.options.forEach((option, index) => {
                // 옵션의 상태 필드가 객체인 경우 (mainImageStatus 등)
                if (option[type] && typeof option[type] === 'object') {
                    option[type].status = status;
                    updateFields[`options.${index}.${type}.status`] = status;
                } else {
                    // 옵션의 상태 필드가 문자열인 경우
                    option[type] = status;
                    updateFields[`options.${index}.${type}`] = status;
                }
            });
        }

        // Vuex를 통해 상태 업데이트 - 저장 시작
        console.log('Vuex store에 상태 업데이트 시작');
        try {
            if (!product._id) throw new Error(`상품 _id 가 없어 상태를 저장할 수 없습니다 (${product.registrationId})`);
            await store.dispatch('updateProductPartial', { productId: product._id, updateFields });
            console.log('Vuex store 상태 업데이트 완료');
            return updatedProduct;
        } catch (error) {
            console.error('Vuex store 상태 업데이트 실패:', error);
            throw error;
        }
    },

    /**
     * 작업 유형에 따른 이름을 반환합니다.
     * @param {string} type - 작업 유형
     * @returns {string} 작업 유형 이름
     */
    getWorkTypeName(type) {
        const typeNames = {
            'mainImageStatus': '대표 이미지',
            'additionalImageStatus': '추가 이미지',
            'detailImageStatus': '상세 이미지',
            'koreanLabelStatus': '한글 표시사항',
            'pricingStatus': '가격 설정',
            'seoStatus': 'SEO 설정',
            'quotationStatus': '견적서',
            'sizeChartStatus': '사이즈표',
        };

        return typeNames[type] || type;
    },

    /**
     * 상품 상태를 업데이트합니다.
     * @param {Object} product - 업데이트할 상품 객체
     * @returns {Promise<boolean>} 업데이트 성공 여부
     */
    async updateProductState(product) {
        try {
            await store.dispatch('updateRocketProduct', product);
            return true;
        } catch (error) {
            console.error('상품 상태 업데이트 중 오류:', error);
            return false;
        }
    },



    /**
     * 확장 프로그램으로부터 받은 진행 상태를 업데이트합니다.
     * @param {Object} data - 진행 상태 데이터
     */
    updateProgress(data) {
        if (!data || !data.text) return;

        store.dispatch('setProcessing', {
            isProcessing: true,
            message: data.text
        });
    },

    /**
     * 확장 프로그램 요청에 대한 타임아웃을 설정합니다.
     */
    setupRequestTimeoutChecker() {
        setInterval(() => {
            const now = Date.now();

            // 5분(300,000ms) 이상 진행 중인 요청 확인
            extensionRequests.forEach((request, id) => {
                if (request.status === 'processing' && now - request.timestamp > 300000) {
                    console.warn(`요청 ${id} (${request.type}) 시간 초과`);
                    request.status = 'timeout';
                    extensionRequests.set(id, request);

                    // 사용자에게 알림
                    store.dispatch('setProcessing', {
                        isProcessing: false,
                        message: `${request.type} 작업 요청이 시간 초과되었습니다.`
                    });
                }
            });

            // 완료된 요청 정리 (10분 이상 지난 요청)
            extensionRequests.forEach((request, id) => {
                if ((request.status === 'completed' || request.status === 'error' || request.status === 'timeout') &&
                    now - (request.completedAt || request.timestamp) > 600000) {
                    extensionRequests.delete(id);
                }
            });
        }, 60000); // 1분마다 체크
    },

    /**
     * 쿠팡에서 연관 키워드를 수집합니다 (개별 비동기 응답 처리)
     * @param {string} targetKeyword - 대상 키워드
     * @param {Object} options - 수집 옵션
     * @returns {Promise<Array>} 수집된 키워드 배열
     */
    collectCoupangKeywords(targetKeyword, options = {}) {
        return new Promise((resolve, reject) => {
            try {
                // 요청 ID 생성
                const requestId = `keyword_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

                console.log(`[collectCoupangKeywords] 키워드 수집 시작 (ID: ${requestId}):`, targetKeyword);

                // 개별 메시지 핸들러 생성
                const messageHandler = (event) => {
                    if (!event.data) return;

                    // 키워드 수집 결과 처리
                    if (event.data.res === 'adkeywords' && event.data.id === requestId) {
                        console.log(`[collectCoupangKeywords] 키워드 수집 결과 수신 (ID: ${requestId}):`, event.data);

                        // 리스너 제거
                        window.removeEventListener('message', messageHandler);
                        clearTimeout(timeoutId);

                        // 결과 데이터 정리
                        const allKeywords = event.data.relatedkeywords || [];
                        const uniqueKeywords = [...new Set(allKeywords)]
                            .filter(keyword => keyword && keyword.trim().length > 0);

                        console.log(`[collectCoupangKeywords] 수집 완료 (ID: ${requestId}):`, uniqueKeywords.length, '개');

                        // 진행 상태 업데이트
                        this.updateProgress({
                            text: '연관 키워드 수집 완료',
                            progress: 100
                        });

                        // Promise 해결
                        resolve(uniqueKeywords);
                    }

                    // 키워드 수집 진행률 처리
                    if (event.data.res === 'relkeywordsrate' && event.data.id === requestId) {
                        this.updateProgress({
                            text: '연관 키워드 수집 중',
                            progress: event.data.rate * 100
                        });
                    }
                };

                // 이벤트 리스너 등록
                window.addEventListener('message', messageHandler);

                // 타임아웃 설정 (30초)
                const timeoutId = setTimeout(() => {
                    window.removeEventListener('message', messageHandler);
                    console.warn(`[collectCoupangKeywords] 키워드 수집 시간 초과 (ID: ${requestId})`);
                    reject(new Error('키워드 수집 시간 초과'));
                }, 30000);

                // 확장 프로그램에 키워드 수집 요청
                window.postMessage({
                    greeting: "getadkeyword",
                    id: requestId,
                    data: [targetKeyword],
                    cnt: options.cycle || 1,
                    relKeywordChecked: options.relatedKeywords !== false,
                    autoCompleteChecked: options.autoComplete !== false
                }, "*");

                // 진행 상태 업데이트
                this.updateProgress({
                    text: '연관 키워드 수집 요청 중...',
                    progress: 10
                });

            } catch (error) {
                console.error(`[collectCoupangKeywords] 오류:`, error);
                reject(error);
            }
        });
    },

    /**
     * 지정된 시간(ms) 동안 대기하는 함수
     * @param {number} ms - 대기 시간(밀리초)
     * @returns {Promise<void>}
     */
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    },

    /**
     * 현재 분당 요청 수를 계산하고 오래된 타임스탬프 제거
     * @returns {number} 현재 분당 요청 수
     */
    updateRequestCount() {
        const now = Date.now();
        const oneMinuteAgo = now - MINUTE_IN_MS;

        // 1분이 지난 타임스탬프 제거
        requestTimestamps = requestTimestamps.filter(timestamp => timestamp > oneMinuteAgo);

        // 현재 분당 요청 수 반환
        return requestTimestamps.length;
    },

    /**
     * 확장 프로그램에 요청을 보내고 Promise로 응답을 반환하는 함수 (큐 관리 및 재시도 로직 포함)
     * @param {Object} requestData - 요청 데이터
     * @param {Object} options - 요청 옵션
     * @param {number} options.timeout - 타임아웃 시간(ms), 기본값 60000ms
     * @param {number} options.maxRetries - 최대 재시도 횟수, 기본값 2
     * @returns {Promise<Object>} 확장 프로그램 응답 결과
     */
    async requestExtensionWithPromise(requestData, options = {}) {
        const timeout = options.timeout || 60000; // 기본 타임아웃 60초
        const maxRetries = options.maxRetries || 2; // 기본 최대 재시도 횟수 2회

        // 서버 Gemini(/api/ai/rocket/gemini)로 바로 — 옛 확장 큐 길은 걷음(cpl-detail-keyleft2a-1)
        const requestId = `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        console.log(`[requestExtensionWithPromise-SERVER] 요청 시작 (ID: ${requestId}):`, {
            workType: requestData.workType,
            task: requestData.task
        });

        const executeServerRequest = async (retryCount = 0) => {
            try {
                // requestArr 구성: Extension과 서버 간 데이터 형식 호환성 처리
                let requestArr = requestData.requestArr || [];

                // AI_TRANSLATE: data 객체를 requestArr 형태로 변환
                if (requestData.workType === 'AI_TRANSLATE' && requestData.data) {
                    const { imgUrl, chArr, prompt } = requestData.data;
                    // 프롬프트에 중국어 배열 정보를 포함시켜 구성
                    const fullPrompt = prompt + '\n\n## 번역할 중국어 텍스트:\n' +
                        JSON.stringify(chArr, null, 2);
                    requestArr = [{
                        prompt: fullPrompt,
                        imageUrls: imgUrl ? [imgUrl] : [],
                        temperature: 0.3 // 번역은 낮은 temperature
                    }];
                    console.log(`[requestExtensionWithPromise-SERVER] AI_TRANSLATE 요청 변환:`, {
                        imgUrl: imgUrl,
                        chArrCount: chArr?.length || 0
                    });
                } else if (requestArr.length > 0) {
                    // 기존 requestArr의 'images' 필드를 'imageUrls'로 변환 (서버 호환성)
                    requestArr = requestArr.map(req => {
                        const converted = { ...req };
                        // images → imageUrls 변환 (Extension은 images, 서버는 imageUrls 사용)
                        if (req.images && !req.imageUrls) {
                            converted.imageUrls = req.images;
                            delete converted.images;
                        }
                        // prompt가 없으면 빈 프롬프트 설정 (SIZE_CHART 등 이미지만 보내는 경우)
                        if (!converted.prompt && converted.imageUrls?.length > 0) {
                            converted.prompt = '이미지를 분석해주세요.';
                        }
                        return converted;
                    });
                }

                const response = await axios.post('/api/ai/rocket/gemini', {
                    workType: requestData.workType,
                    task: requestData.task || null,
                    requestArr: requestArr,
                    productId: requestData.productId || (store.state.selectedRocketProduct ? store.state.selectedRocketProduct.registrationId : null)
                }, {
                    timeout: timeout
                });

                console.log(`[requestExtensionWithPromise-SERVER] 응답 수신 (ID: ${requestId}):`, {
                    workType: response.data.workType,
                    task: response.data.task,
                    hasResult: !!response.data.result
                });

                if (!response.data.success) {
                    throw new Error(response.data.error || '서버 Gemini 요청 실패');
                }

                return response.data.result;

            } catch (error) {
                const errorMessage = error.response?.data?.error || error.message || error.toString();
                console.error(`[requestExtensionWithPromise-SERVER] 오류 (ID: ${requestId}, retry: ${retryCount}):`, errorMessage);

                // 치명적 오류는 재시도하지 않음
                const isFatalError = errorMessage.includes('401') ||
                                    errorMessage.includes('403') ||
                                    errorMessage.includes('로그인') ||
                                    errorMessage.includes('권한');

                if (isFatalError) {
                    throw new Error(errorMessage);
                }

                // 429 (rate limit)는 잠시 대기 후 재시도
                if (error.response?.status === 429 && retryCount < maxRetries) {
                    const delay = Math.min(2000 * Math.pow(2, retryCount), 10000);
                    console.warn(`[requestExtensionWithPromise-SERVER] Rate limit, ${delay}ms 후 재시도...`);
                    await this.sleep(delay);
                    return executeServerRequest(retryCount + 1);
                }

                // 기타 오류 재시도
                if (retryCount < maxRetries) {
                    const delay = Math.min(1000 * Math.pow(2, retryCount), 10000);
                    console.warn(`[requestExtensionWithPromise-SERVER] 요청 실패 (${retryCount + 1}/${maxRetries}), ${delay}ms 후 재시도...`);
                    await this.sleep(delay);
                    return executeServerRequest(retryCount + 1);
                }

                throw new Error(errorMessage);
            }
        };

        return executeServerRequest(0);
    },

    /**
     * 현재 큐 상태 정보 반환 (디버깅용)
     * @returns {Object} 큐 상태 정보
     */
    getQueueStatus() {
        return {
            queueLength: requestQueue.length,
            processingRequest: processingRequest,
            requestsInLastMinute: this.updateRequestCount(),
            rateLimit: RATE_LIMIT_PER_MINUTE
        };
    },

    /**
     * 큐를 비우고 대기 중인 모든 요청을 취소합니다.
     * Gemini API 오류 등으로 더 이상 진행이 불가능할 때 사용합니다.
     */
    clearQueue() {
        console.log(`[Queue Clear] 큐 비우기 시작 - 대기 중인 요청: ${requestQueue.length}개`);

        // 대기 중인 모든 요청을 reject 처리
        while (requestQueue.length > 0) {
            const request = requestQueue.shift();
            if (request && request.reject) {
                request.reject(new Error('Queue cleared due to Gemini API error'));
            }
        }

        console.log('[Queue Clear] 큐 비우기 완료');
    },

    /**
     * 서비스를 초기화합니다.
     */
    init() {
        this.setupRequestTimeoutChecker();
        console.log('워크플로우 서비스 초기화 완료');
    },

    /**
     * 등록 검증 에러를 AI로 자동 수정합니다.
     * @param {Object} product - 상품 객체
     * @param {Object} fullErrorData - 전체 에러 데이터 (errors, cellIndexToSchemaFieldMap 포함)
     * @returns {Promise<Object>} 수정된 상품 객체
     */
    async fixRegistrationErrors(product, fullErrorData) {
        product = await ensureFullProduct(product);
        try {
            console.log('🔧 AI 기반 등록 에러 자동 수정 시작');
            console.log('에러 데이터:', fullErrorData);

            const { errors, cellIndexToSchemaFieldMap } = fullErrorData;

            if (!errors || errors.length === 0) {
                console.warn('⚠️ 에러 정보가 없습니다.');
                return product;
            }

            // 0. "한 개 단품 포장 사이즈" 에러 자동 수정 (1번 AI 시도 후 실패 시에만 기본값 적용)
            const boxSizeErrors = errors.filter(error =>
                error.columnName && error.columnName.includes('한 개 단품 포장 사이즈')
            );

            // aiFixRetryCount가 1 이상이면 (즉, 1번 AI 시도 후 실패한 경우) 기본값 적용
            if (boxSizeErrors.length > 0 && product.aiFixRetryCount >= 1) {
                console.log(`📦 "한 개 단품 포장 사이즈" 에러 ${boxSizeErrors.length}개 발견 - AI 1번 시도 후 실패하여 기본값 적용`);

                boxSizeErrors.forEach(error => {
                    const docItem = product.coupangDoc.jsonDocument[error.rowIndex];
                    if (docItem && docItem.logisticsPage) {
                        const defaultValue = "100*100*100";
                        docItem.logisticsPage.skuUnitBoxDimension = defaultValue;
                        console.log(`  ✅ 옵션 ${error.rowIndex} 한 개 단품 포장 사이즈 기본값 설정: ${defaultValue}`);
                    }
                });

                // "한 개 단품 포장 사이즈" 에러를 errors 배열에서 제거 (AI 수정 불필요)
                const remainingErrors = errors.filter(error =>
                    !error.columnName || !error.columnName.includes('한 개 단품 포장 사이즈')
                );

                if (remainingErrors.length === 0) {
                    console.log('✅ 모든 에러가 자동 수정되었습니다 (AI 수정 불필요)');
                    return product;
                }

                // 남은 에러만 AI로 수정
                fullErrorData.errors = remainingErrors;
            }

            // 1. rowIndex별로 에러 그룹화
            const errorsByRow = {};
            fullErrorData.errors.forEach(error => {
                if (!errorsByRow[error.rowIndex]) {
                    errorsByRow[error.rowIndex] = [];
                }
                errorsByRow[error.rowIndex].push({
                    ...error,
                    schemaInfo: cellIndexToSchemaFieldMap?.[error.columnIndex] || null
                });
            });

            console.log(`📊 에러 분석: ${Object.keys(errorsByRow).length}개 옵션에서 에러 발생`);

            // 2. 각 옵션별로 에러 수정
            for (const [rowIndex, rowErrors] of Object.entries(errorsByRow)) {
                const option = product.options[rowIndex];
                const optionDoc = product.coupangDoc.jsonDocument[rowIndex];

                if (!option || !optionDoc) {
                    console.warn(`⚠️ rowIndex ${rowIndex}에 해당하는 옵션 또는 문서가 없습니다.`);
                    continue;
                }

                console.log(`🔄 옵션 ${rowIndex} (${option.korName}) 에러 수정 시작 - ${rowErrors.length}개 에러`);

                // 3. AI에게 수정 요청
                const fixedData = await this.requestAIFix(product, option, optionDoc, rowErrors);

                if (fixedData && fixedData.fixes && fixedData.fixes.length > 0) {
                    // 4. 수정된 데이터 적용
                    this.applyFixedData(product, rowIndex, fixedData);
                    console.log(`✅ 옵션 ${rowIndex} 에러 수정 완료`);
                } else {
                    console.warn(`⚠️ 옵션 ${rowIndex} AI 수정 실패 - 응답 없음`);
                }
            }

            console.log('✅ AI 기반 등록 에러 자동 수정 완료');
            return product;

        } catch (error) {
            console.error('❌ AI 기반 등록 에러 수정 중 오류:', error);
            throw error;
        }
    },

    /**
     * AI에게 에러 수정 요청
     * @param {Object} product - 상품 객체
     * @param {Object} option - 옵션 객체
     * @param {Object} optionDoc - 옵션 문서 (coupangDoc.jsonDocument[rowIndex])
     * @param {Array} rowErrors - 해당 옵션의 에러 배열
     * @returns {Promise<Object>} AI 수정 결과
     */
    async requestAIFix(product, option, optionDoc, rowErrors) {
        try {
            console.log(`🤖 AI 에러 수정 요청: 옵션 ${option.optionNumber}`);

            // 에러 정보 포맷팅
            const errorDescriptions = rowErrors.map(error => {
                const currentValue = this.getCurrentValue(optionDoc, error.schemaInfo);
                return `
### ${error.columnName}
- **현재 값**: ${currentValue || '(없음)'}
- **에러 메시지**: ${error.message}
- **필드 타입**: ${error.schemaInfo?.baseFieldName || '알 수 없음'}
- **스키마 경로**: ${error.schemaInfo?.fieldName || '알 수 없음'}`;
            }).join('\n');

            // AI 프롬프트 구성
            const prompt = `
# 쿠팡 상품 등록 검증 에러 수정

## 상품 정보
- 상품명: ${product.productName}
- 옵션명: ${option.korName}
- 옵션번호: ${option.optionNumber}

## 원본 1688 상품 정보 (참고용)
\`\`\`json
${JSON.stringify({
    title: product.sourceData?.title || product.productName,
    attributes: product.sourceData?.attributes || [],
    specs: product.sourceData?.specs || []
}, null, 2)}
\`\`\`

## 수정이 필요한 필드들 (에러 발생)
${errorDescriptions}

## 작업 요구사항

위에 나열된 **에러 필드들만** 수정해주세요. 나머지 필드는 수정하지 마세요.

각 필드에 대해:
1. 에러 메시지를 정확히 분석
2. "사용 가능한 값/단위"가 명시되어 있으면 **반드시** 그 중에서 선택
3. 원본 상품 정보를 참고하여 올바른 값 생성
4. 값이 불명확하면 "상세설명 참조" 또는 null 사용

## 응답 형식 (JSON만 출력)

\`\`\`json
{
  "fixes": [
    {
      "columnName": "가로길이",
      "fieldType": "unexposedAttributes",
      "attributeName": "가로길이",
      "newValue": "50cm",
      "reason": "에러 메시지에서 cm 단위 사용 가능하다고 명시됨"
    }
  ]
}
\`\`\`

**중요**:
- JSON 형식만 출력 (설명 없이)
- 에러가 발생한 필드만 수정
- fieldType은 다음 중 하나: exposedAttributes, unexposedAttributes, notices, certificates, 또는 단일 필드명 (brand, manufacturer 등)
- 속성 배열인 경우 attributeName 필수
`;

            // Gemini API 호출
            const result = await this.requestExtensionWithPromise({
                workType: 'FIX_REGISTRATION_ERRORS',
                productId: product.registrationId,
                requestArr: [{
                    prompt: prompt,
                    temperature: 0.1  // 낮은 temperature로 일관성 확보
                }]
            }, {
                timeout: 60000,
                maxRetries: 2
            });

            console.log('🤖 AI 응답:', result);

            // 응답 파싱
            return this.parseAIFixResult(result);

        } catch (error) {
            console.error('❌ AI 에러 수정 요청 중 오류:', error);
            throw error;
        }
    },

    /**
     * AI 응답 파싱
     * @param {Object} result - AI 응답 결과
     * @returns {Object} 파싱된 수정 데이터
     */
    parseAIFixResult(result) {
        try {
            if (!result || !result.batchResults || result.batchResults.length === 0) {
                console.warn('⚠️ AI 응답이 비어있습니다.');
                return null;
            }

            const aiResponse = result.batchResults[0];
            console.log('📝 AI 원본 응답:', aiResponse);

            // ✅ aiResponse가 객체인 경우 processedText 추출
            let responseText = aiResponse;
            if (typeof aiResponse === 'object' && aiResponse.processedText) {
                responseText = aiResponse.processedText;
                console.log('📝 processedText 추출:', responseText);
            }

            // JSON 추출 (```json ... ``` 형식 처리)
            let jsonText = responseText;

            // 문자열인 경우에만 match 사용
            if (typeof responseText === 'string') {
                const jsonMatch = responseText.match(/```json\s*([\s\S]*?)\s*```/);
                if (jsonMatch) {
                    jsonText = jsonMatch[1];
                } else {
                    // ``` 없이 바로 JSON인 경우
                    const plainJsonMatch = responseText.match(/\{[\s\S]*\}/);
                    if (plainJsonMatch) {
                        jsonText = plainJsonMatch[0];
                    }
                }
            }

            const parsed = JSON.parse(jsonText);
            console.log('✅ AI 응답 파싱 완료:', parsed);

            return parsed;

        } catch (error) {
            console.error('❌ AI 응답 파싱 실패:', error);
            console.error('원본 응답:', result);
            return null;
        }
    },

    /**
     * 수정된 데이터를 상품에 적용
     * @param {Object} product - 상품 객체
     * @param {number} rowIndex - 옵션 인덱스
     * @param {Object} fixedData - AI가 수정한 데이터
     */
    applyFixedData(product, rowIndex, fixedData) {
        try {
            const docItem = product.coupangDoc.jsonDocument[rowIndex];

            if (!docItem) {
                console.error(`❌ rowIndex ${rowIndex}에 해당하는 문서가 없습니다.`);
                return;
            }

            console.log(`🔧 수정 데이터 적용 시작: 옵션 ${rowIndex}`);

            fixedData.fixes.forEach(fix => {
                console.log(`  - ${fix.columnName}: ${fix.newValue} (이유: ${fix.reason})`);

                if (fix.fieldType === 'exposedAttributes') {
                    // 노출속성 배열에서 해당 속성만 찾아서 수정
                    const attr = docItem.productPage?.commonAttributes?.exposedAttributes?.find(
                        a => a.attributeName === fix.attributeName
                    );
                    if (attr) {
                        attr.attributeValue = fix.newValue;
                        console.log(`    ✅ 노출속성 수정: ${fix.attributeName} = ${fix.newValue}`);
                    } else {
                        console.warn(`    ⚠️ 노출속성 "${fix.attributeName}"을 찾을 수 없습니다.`);
                    }

                } else if (fix.fieldType === 'unexposedAttributes') {
                    // 비노출속성 배열에서 해당 속성만 찾아서 수정
                    const attr = docItem.productPage?.unexposedAttributes?.find(
                        a => a.attributeName === fix.attributeName
                    );
                    if (attr) {
                        attr.attributeValue = fix.newValue;
                        console.log(`    ✅ 비노출속성 수정: ${fix.attributeName} = ${fix.newValue}`);
                    } else {
                        console.warn(`    ⚠️ 비노출속성 "${fix.attributeName}"을 찾을 수 없습니다.`);
                    }

                } else if (fix.fieldType === 'notices') {
                    // 법적고시 배열에서 해당 항목만 찾아서 수정
                    const notice = docItem.legalPage?.notices?.find(
                        n => n.noticeItemName === fix.attributeName
                    );
                    if (notice) {
                        notice.noticeItemValue = fix.newValue;
                        console.log(`    ✅ 법적고시 수정: ${fix.attributeName} = ${fix.newValue}`);
                    } else {
                        console.warn(`    ⚠️ 법적고시 "${fix.attributeName}"을 찾을 수 없습니다.`);
                    }

                } else if (fix.fieldType === 'certificates') {
                    // 인증정보 배열에서 해당 항목만 찾아서 수정
                    const cert = docItem.legalPage?.certificates?.find(
                        c => c.certificateName === fix.attributeName
                    );
                    if (cert) {
                        cert.certificateValue = fix.newValue;
                        console.log(`    ✅ 인증정보 수정: ${fix.attributeName} = ${fix.newValue}`);
                    } else {
                        console.warn(`    ⚠️ 인증정보 "${fix.attributeName}"을 찾을 수 없습니다.`);
                    }

                } else {
                    // 단일 필드 수정 (brand, manufacturer 등)
                    if (docItem.productPage?.[fix.fieldType] !== undefined) {
                        docItem.productPage[fix.fieldType] = fix.newValue;
                        console.log(`    ✅ 단일 필드 수정: ${fix.fieldType} = ${fix.newValue}`);
                    } else if (docItem.productPage?.commonAttributes?.[fix.fieldType] !== undefined) {
                        docItem.productPage.commonAttributes[fix.fieldType] = fix.newValue;
                        console.log(`    ✅ 공통속성 필드 수정: ${fix.fieldType} = ${fix.newValue}`);
                    } else {
                        console.warn(`    ⚠️ 필드 "${fix.fieldType}"을 찾을 수 없습니다.`);
                    }
                }
            });

            console.log(`✅ 수정 데이터 적용 완료: 옵션 ${rowIndex} - ${fixedData.fixes.length}개 필드 수정`);

        } catch (error) {
            console.error('❌ 수정 데이터 적용 중 오류:', error);
        }
    },

    /**
     * 옵션 문서에서 현재 값 가져오기
     * @param {Object} optionDoc - 옵션 문서
     * @param {Object} schemaInfo - 스키마 정보
     * @returns {string} 현재 값
     */
    getCurrentValue(optionDoc, schemaInfo) {
        try {
            if (!schemaInfo || !schemaInfo.baseFieldName) {
                return null;
            }

            const fieldType = schemaInfo.baseFieldName;
            const columnName = schemaInfo.title;

            if (fieldType === 'exposedAttributes') {
                const attr = optionDoc.productPage?.commonAttributes?.exposedAttributes?.find(
                    a => a.attributeName === columnName
                );
                return attr?.attributeValue || null;

            } else if (fieldType === 'unexposedAttributes') {
                const attr = optionDoc.productPage?.unexposedAttributes?.find(
                    a => a.attributeName === columnName
                );
                return attr?.attributeValue || null;

            } else if (fieldType === 'notices') {
                const notice = optionDoc.legalPage?.notices?.find(
                    n => n.noticeItemName === columnName
                );
                return notice?.noticeItemValue || null;

            } else if (fieldType === 'certificates') {
                const cert = optionDoc.legalPage?.certificates?.find(
                    c => c.certificateName === columnName
                );
                return cert?.certificateValue || null;

            } else {
                // 단일 필드
                return optionDoc.productPage?.[fieldType] ||
                       optionDoc.productPage?.commonAttributes?.[fieldType] ||
                       null;
            }

        } catch (error) {
            console.error('현재 값 가져오기 실패:', error);
            return null;
        }
    },

    /**
     * 작업 타입에 대응하는 상태 필드명 반환
     * @param {string} task - 작업 타입
     * @returns {string} 상태 필드명
     */
    getStatusFieldForTask(task) {
        const taskToStatusField = {
            'seo': 'seoStatus',
            'pricing': 'pricingStatus',
            'mainImage': 'mainImageStatus',
            'additionalImage': 'additionalImageStatus',
            'detailImage': 'detailImageStatus',
            'sizeChart': 'sizeChartStatus',
            'koreanLabel': 'koreanLabelStatus',
            'quotation': 'quotationStatus'
        };
        return taskToStatusField[task] || task + 'Status';
    }
};

// 서비스 초기화
workflowService.init();

export default workflowService;